/**
 * Clients — list + create/edit/delete modal.
 *
 * Modelo de jerarquía (dropshipping):
 *   Un cliente puede ser `mayorista` y tener clientes finales ligados (hijos)
 *   que reciben el envío directo. Cada hijo es a su vez un cliente completo
 *   con su propio historial. La relación se guarda en `parentClientId` del
 *   hijo. Profundidad máxima = 2 (un hijo no puede tener hijos).
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  Link2,
  Link2Off,
  MapPin,
  Pencil,
  Plus,
  Trash2,
  Users,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  CLIENT_TYPES,
  CLIENT_TYPE_LEGACY_MAP,
} from '@expogreens/shared'
import { ModuleShell } from '@/components/ui/ModuleShell'
import { Segmented } from '@/components/ui/Segmented'
import { FilterBar } from '@/components/ui/FilterBar'
import { SearchButton } from '@/components/ui/SearchButton'
import { Button } from '@/components/ui/Button'
import { Modal, Field, Input, Select } from '@/components/ui/Modal'
import { useApi } from '@/lib/useApi'
import { useMutation } from '@/lib/useMutation'
import { apiCall } from '@/lib/api'

const labelForType = (t) => CLIENT_TYPES[t] || t || '—'
const canonicalType = (t) => CLIENT_TYPE_LEGACY_MAP[t] || t || 'principal'

// Badge de tipo estilo handoff (#28) para la tabla de clientes.
const CLIENT_TYPE_BADGE = {
  principal: { bg: '#E3F0D5', fg: '#1E6E3A' },
  consignee: { bg: '#E7ECFB', fg: '#3B4FB0' },
}
function clientTypeBadgeStyle(t) {
  const c = CLIENT_TYPE_BADGE[canonicalType(t)] || { bg: '#F0F3EE', fg: '#5C6B60' }
  return {
    display: 'inline-flex',
    alignItems: 'center',
    background: c.bg,
    color: c.fg,
    fontSize: 13,
    fontWeight: 700,
    padding: '5px 12px',
    borderRadius: 999,
    whiteSpace: 'nowrap',
  }
}

// País derivado del prefijo telefónico (no hay campo país en el modelo).
const PHONE_COUNTRY = {
  '+1': 'Estados Unidos',
  '+31': 'Países Bajos',
  '+33': 'Francia',
  '+34': 'España',
  '+39': 'Italia',
  '+44': 'Reino Unido',
  '+49': 'Alemania',
  '+52': 'México',
  '+57': 'Colombia',
  '+81': 'Japón',
}
function countryOf(c) {
  const phone = (c?.phone || '').trim()
  for (const code of Object.keys(PHONE_COUNTRY).sort((a, b) => b.length - a.length)) {
    if (phone.startsWith(code)) return PHONE_COUNTRY[code]
  }
  return null
}

const TIPO_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'principal', label: 'Principal' },
  { value: 'consignee', label: 'Consignatario' },
]

/** Iniciales (máx. 2) para el chip de avatar de la fila. */
function initialsOf(name = '') {
  return name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

// Tabla #28 reducida a una sola columna de datos (Cliente + Tipo) + chevron.
const CLIENTS_GRID = '2.6fr 1fr 40px'

// Opciones para el selector visual del tipo de cliente. Cada una con
// icono propio para que el usuario las distinga de un vistazo sin abrir
// un dropdown.
const CLIENT_TYPE_OPTIONS = [
  {
    id: 'principal',
    label: 'Principal',
    icon: Building2,
    description: 'Cliente principal — puede tener consignatarios ligados.',
  },
  {
    id: 'consignee',
    label: 'Consignatario',
    icon: MapPin,
    description: 'Ubicación/consignatario asociado a un cliente Principal.',
  },
]

const EMPTY = {
  name: '',
  email: '',
  phone: '',
  nit: '',
  address: '',
  clientType: 'principal',
  parentClientId: '',
}

export default function ClientsPage() {
  const { data, loading, error, refresh } = useApi('/clients')
  const [editing, setEditing] = useState(null) // null | EMPTY | existing row
  const [selectedId, setSelectedId] = useState(null) // fila activa (maestro-detalle)

  // Estado del modal: padre propuesto + hijos propuestos (por id). Se
  // separan del estado del form principal porque los hijos viven en
  // registros OTROS (no en este cliente), y se aplican via PUT por cada
  // hijo al guardar.
  const [parentId, setParentId] = useState('')
  const [childIds, setChildIds] = useState([])
  const [childPick, setChildPick] = useState('')
  // Tipo de cliente — controlado para soportar el selector visual con
  // tarjetas-botón (en vez de un <select>).
  const [clientType, setClientType] = useState('principal')

  // Sincronizar el tipo con el cliente que se está editando.
  useEffect(() => {
    if (!editing) return
    setClientType(canonicalType(editing.clientType))
  }, [editing])

  // Auto-coherencia: si el usuario marca al cliente como Consignatario
  // de uno principal, su tipo pasa a 'consignee'. Y al revés, si saca
  // el padre, vuelve a 'principal'. Esto evita estados inconsistentes
  // sin que el usuario tenga que recordar tocar dos campos.
  useEffect(() => {
    if (parentId && clientType !== 'consignee') setClientType('consignee')
    if (!parentId && clientType === 'consignee') setClientType('principal')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId])

  const save = useMutation(async (payload) => {
    if (payload.id) {
      return apiCall(`/clients/${payload.id}`, { method: 'PUT', body: payload })
    }
    return apiCall('/clients', { method: 'POST', body: payload })
  })

  const remove = useMutation(async (id) => apiCall(`/clients/${id}`, { method: 'DELETE' }))

  // Mapa id→cliente para resolver nombres rápido.
  const byId = useMemo(() => {
    const m = new Map()
    for (const c of data || []) m.set(c.id, c)
    return m
  }, [data])

  // Filtros: búsqueda (nombre/id/email), tipo y país.
  const [query, setQuery] = useState('')
  const [filterTipo, setFilterTipo] = useState('all')
  const [filterPais, setFilterPais] = useState('all')

  // Opciones de país detectadas en los datos (por prefijo telefónico).
  const paisFilters = useMemo(() => {
    const set = new Set()
    for (const c of data || []) {
      const p = countryOf(c)
      if (p) set.add(p)
    }
    return [
      { value: 'all', label: 'Todos' },
      ...Array.from(set).sort((a, b) => a.localeCompare(b, 'es')).map((p) => ({ value: p, label: p })),
    ]
  }, [data])

  const visibleClients = useMemo(() => {
    const list = data || []
    const q = query.trim().toLowerCase()
    return list.filter((c) => {
      if (filterTipo !== 'all' && canonicalType(c.clientType) !== filterTipo) return false
      if (filterPais !== 'all' && countryOf(c) !== filterPais) return false
      if (!q) return true
      return `${c.name || ''} ${c.id || ''} ${c.email || ''}`.toLowerCase().includes(q)
    })
  }, [data, query, filterTipo, filterPais])

  // ¿Este cliente es padre de alguien? (mientras lo sea no puede tener padre)
  const hasChildren = (clientId) =>
    (data || []).some((c) => c.parentClientId === clientId)

  const childrenOf = (clientId) =>
    (data || []).filter((c) => c.parentClientId === clientId)

  const openCreate = () => {
    setEditing({ ...EMPTY })
    setParentId('')
    setChildIds([])
    setChildPick('')
  }

  const openEdit = (row) => {
    setEditing(row)
    setParentId(row.parentClientId || '')
    setChildIds(childrenOf(row.id).map((c) => c.id))
    setChildPick('')
  }

  const closeModal = () => {
    setEditing(null)
    setParentId('')
    setChildIds([])
    setChildPick('')
  }

  // Candidatos a mayorista (padre): clientes ≠ self que NO son hijos de otro
  // y que no están en la lista de hijos propuestos del actual (no pueden ser
  // padre de su propio padre).
  const parentCandidates = useMemo(() => {
    if (!editing) return []
    return (data || []).filter((c) => {
      if (editing.id && c.id === editing.id) return false
      if (c.parentClientId) return false
      if (childIds.includes(c.id)) return false
      return true
    })
  }, [data, editing, childIds])

  // Candidatos a hijo: clientes ≠ self, no ya seleccionados, sin padre (para
  // no quitarle el padre a otro), y ellos mismos no son padres de nadie (o
  // sea, no tienen hijos).
  const childCandidates = useMemo(() => {
    if (!editing) return []
    return (data || []).filter((c) => {
      if (editing.id && c.id === editing.id) return false
      if (childIds.includes(c.id)) return false
      if (c.parentClientId && c.parentClientId !== editing.id) return false
      if (hasChildren(c.id)) return false
      // Si yo mismo tengo padre, no puedo tener hijos.
      if (parentId) return false
      return true
    })
  }, [data, editing, childIds, parentId]) // eslint-disable-line react-hooks/exhaustive-deps

  const addChild = () => {
    if (!childPick) return
    setChildIds((l) => (l.includes(childPick) ? l : [...l, childPick]))
    setChildPick('')
  }
  const removeChild = (id) => setChildIds((l) => l.filter((x) => x !== id))

  const onSubmit = async (e) => {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const payload = Object.fromEntries(fd.entries())
    if (editing?.id) payload.id = editing.id
    payload.parentClientId = parentId || null
    // El tipo viene del selector visual (state) — sobrescribe cualquier
    // resto antiguo que pudiera venir del FormData.
    payload.clientType = clientType

    try {
      const saved = await save.mutate(payload)
      const selfId = saved?.id || editing?.id

      // Aplicar cambios en hijos — solo tiene sentido si YO no tengo padre.
      if (!parentId && selfId) {
        const previousChildren = childrenOf(selfId).map((c) => c.id)
        const toAdd = childIds.filter((id) => !previousChildren.includes(id))
        const toRemove = previousChildren.filter((id) => !childIds.includes(id))

        for (const id of toAdd) {
          const child = byId.get(id)
          if (!child) continue
          await apiCall(`/clients/${id}`, {
            method: 'PUT',
            body: { ...child, parentClientId: selfId },
          })
        }
        for (const id of toRemove) {
          const child = byId.get(id)
          if (!child) continue
          await apiCall(`/clients/${id}`, {
            method: 'PUT',
            body: { ...child, parentClientId: null },
          })
        }
      }

      toast.success(editing?.id ? 'Cliente actualizado' : 'Cliente creado')
      closeModal()
      refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onDelete = async (id) => {
    const children = childrenOf(id)
    const warn = children.length
      ? `Este cliente tiene ${children.length} cliente(s) final(es). Al eliminarlo, quedarán desvinculados (no se borrarán). ¿Continuar?`
      : '¿Eliminar este cliente?'
    if (!confirm(warn)) return
    try {
      await remove.mutate(id)
      toast.success('Cliente eliminado')
      refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  return (
    <>
      <ModuleShell
        title="Clientes"
        subtitle="Clientes Principales → Consignatarios ligados (ubicaciones de envío)"
        icon={Users}
        action={
          <div className="flex items-center gap-3">
            <SearchButton value={query} onChange={setQuery} placeholder="Buscar cliente…" />
            <Button onClick={openCreate} className="!px-3 sm:!px-6">
              <Plus size={20} />
              <span className="hidden sm:inline">Nuevo cliente</span>
            </Button>
          </div>
        }
        filters={
          paisFilters.length > 1 ? (
            <FilterBar
              left={<Segmented options={paisFilters} value={filterPais} onChange={setFilterPais} />}
              right={<Segmented options={TIPO_FILTERS} value={filterTipo} onChange={setFilterTipo} />}
            />
          ) : (
            <FilterBar left={<Segmented options={TIPO_FILTERS} value={filterTipo} onChange={setFilterTipo} />} />
          )
        }
      >

      {loading && <p className="text-expo-muted">Cargando…</p>}
      {error && <p className="text-red-600">Error: {error.message}</p>}
      {data && (
        <div className="space-y-6">
          {/* Tabla #28 — una sola columna de datos (Cliente + Tipo) */}
          <div style={{ background: '#fff', border: '1px solid #E6EFE3', borderRadius: 22, overflow: 'hidden' }}>
            <div
              className="flex items-start justify-between gap-6 flex-wrap"
              style={{ padding: '28px 30px 22px', borderBottom: '1.5px solid #EAF0E6' }}
            >
              <div>
                <div className="font-display" style={{ fontSize: 28, fontWeight: 800, color: '#143A22', letterSpacing: '-0.01em', lineHeight: 1.05 }}>
                  Clientes registrados
                </div>
                <div style={{ fontSize: 15, color: '#8B9B8F', fontWeight: 500, marginTop: 4 }}>
                  Principales y consignatarios ligados
                </div>
              </div>
              <div style={{ textAlign: 'right', flex: 'none' }}>
                <div className="font-display" style={{ fontSize: 30, fontWeight: 800, color: '#033021', lineHeight: 1 }}>
                  {visibleClients.length}
                </div>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', color: '#9BA89B', marginTop: 5 }}>
                  CLIENTES · TOTAL
                </div>
              </div>
            </div>

            <div
              className="grid uppercase"
              style={{ gridTemplateColumns: CLIENTS_GRID, gap: 16, padding: '14px 30px', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', color: '#A9B4AA' }}
            >
              <span>Cliente</span>
              <span>Tipo</span>
              <span />
            </div>

            {visibleClients.length === 0 ? (
              <div style={{ padding: '48px 30px', textAlign: 'center', color: '#8B9B8F', fontSize: 14 }}>
                Sin clientes registrados
              </div>
            ) : (
              visibleClients.map((r) => {
                const parent = r.parentClientId ? byId.get(r.parentClientId) : null
                const kids = childrenOf(r.id)
                const meta = parent
                  ? `hijo de ${parent.name}`
                  : kids.length
                    ? `${kids.length} consignatario${kids.length === 1 ? '' : 's'}`
                    : 'cliente directo'
                const open = r.id === selectedId
                return (
                  <div key={r.id} style={{ borderTop: '1px solid #F2F5F0' }}>
                    {/* Fila (abre/cierra) */}
                    <div
                      onClick={() => setSelectedId(open ? null : r.id)}
                      className="grid items-center cursor-pointer"
                      style={{ gridTemplateColumns: CLIENTS_GRID, gap: 16, padding: '16px 30px', background: open ? '#F4F9EC' : 'transparent' }}
                      onMouseEnter={(e) => { if (!open) e.currentTarget.style.background = '#FAFCF6' }}
                      onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = 'transparent' }}
                    >
                      <div className="flex items-center min-w-0" style={{ gap: 15 }}>
                        <div
                          className="flex items-center justify-center flex-none"
                          style={{ width: 46, height: 46, borderRadius: 13, background: '#033021', color: '#C3DB5A', fontWeight: 800, fontSize: 16 }}
                        >
                          {initialsOf(r.name)}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate" style={{ fontSize: 19, fontWeight: 700, color: '#143A22' }}>
                            {r.name}
                          </div>
                          <div className="truncate" style={{ fontSize: 13, fontWeight: 500, color: '#8B9B8F', marginTop: 2 }}>
                            {r.id} · {meta}
                            {r.email ? ` · ${r.email}` : ''}
                          </div>
                        </div>
                      </div>
                      <span>
                        <span style={clientTypeBadgeStyle(r.clientType)}>{labelForType(r.clientType)}</span>
                      </span>
                      <span className="flex items-center justify-center" style={{ color: '#1E6E3A' }}>
                        {open ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                      </span>
                    </div>

                    {/* Detalle (#45 · ficha de cliente) */}
                    {open && (
                      <div style={{ borderTop: '1px solid #F2F5F0', background: '#FCFEFA', padding: '24px 30px' }}>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-5">
                          {[
                            ['ID', r.id],
                            ['Email', r.email],
                            ['Teléfono', r.phone],
                            ['NIT', r.nit],
                            ['Dirección', r.address],
                            ['Tipo', labelForType(r.clientType)],
                          ].map(([label, value]) => (
                            <div key={label} className="min-w-0">
                              <div className="uppercase" style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.06em', color: '#9BA89B', marginBottom: 6 }}>
                                {label}
                              </div>
                              <div className="break-words" style={{ fontSize: 18, fontWeight: 700, color: '#143A22' }}>
                                {value || '—'}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Jerarquía */}
                        <div style={{ marginTop: 24, border: '1px solid #EEF2EC', background: '#fff', borderRadius: 16, padding: 18 }}>
                          <div className="flex items-center gap-2" style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#1E6E3A', marginBottom: 10 }}>
                            <Link2 size={16} />
                            Jerarquía
                          </div>
                          {parent ? (
                            <div style={{ fontSize: 15, color: '#3F4F44' }}>
                              Consignatario de{' '}
                              <span style={{ fontWeight: 800, color: '#1E6E3A' }}>{parent.name}</span>{' '}
                              <span style={{ color: '#8B9B8F' }}>({parent.id})</span>.
                            </div>
                          ) : kids.length > 0 ? (
                            <>
                              <div style={{ fontSize: 15, color: '#3F4F44', marginBottom: 10 }}>
                                Cliente principal con {kids.length} consignatario{kids.length === 1 ? '' : 's'}:
                              </div>
                              <div className="flex flex-col" style={{ gap: 6 }}>
                                {kids.map((k) => (
                                  <div key={k.id} className="flex items-center" style={{ gap: 9, fontSize: 14, color: '#3F4F44' }}>
                                    <span style={{ background: '#E3F0D5', color: '#1E6E3A', fontSize: 12, fontWeight: 800, padding: '3px 9px', borderRadius: 999 }}>{k.id}</span>
                                    <span style={{ fontWeight: 700, color: '#143A22' }}>{k.name}</span>
                                    {k.address && <span style={{ color: '#8B9B8F', fontSize: 13 }}>· {k.address}</span>}
                                  </div>
                                ))}
                              </div>
                            </>
                          ) : (
                            <div style={{ fontSize: 15, color: '#8B9B8F' }}>Sin jerarquía (cliente directo).</div>
                          )}
                        </div>

                        <div className="flex gap-3" style={{ marginTop: 20 }}>
                          <Button variant="secondary" size="lg" onClick={() => openEdit(r)}>
                            <Pencil size={18} />
                            <span>Editar</span>
                          </Button>
                          <Button variant="danger" size="lg" onClick={() => onDelete(r.id)}>
                            <Trash2 size={18} />
                            <span>Eliminar</span>
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}
      </ModuleShell>

      <Modal
        open={!!editing}
        onClose={closeModal}
        title={editing?.id ? 'Editar cliente' : 'Nuevo cliente'}
        subtitle="Datos de contacto y jerarquía"
        size="lg"
      >
        {editing && (
          <form onSubmit={onSubmit} className="space-y-4">
            <Field label="Nombre" required>
              <Input name="name" defaultValue={editing.name} required />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Email">
                <Input type="email" name="email" defaultValue={editing.email} />
              </Field>
              <Field label="Teléfono">
                <Input name="phone" defaultValue={editing.phone} />
              </Field>
            </div>
            <Field label="NIT / ID fiscal">
              <Input name="nit" defaultValue={editing.nit} />
            </Field>
            <Field label="Dirección">
              <Input name="address" defaultValue={editing.address} />
            </Field>
            <Field
              label="Tipo de cliente"
              hint="Solo dos categorías: Principal o Consignatario."
            >
              {/*
                Selector visual con dos tarjetas-botón. Más rápido y
                claro que un dropdown — el usuario ve ambas opciones a
                la vez con icono propio y elige con un click.
              */}
              <div className="grid grid-cols-2 gap-3">
                {CLIENT_TYPE_OPTIONS.map((opt) => {
                  const Icon = opt.icon
                  const active = clientType === opt.id
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => {
                        setClientType(opt.id)
                        // Si elige "Principal", se quita el parent
                        // automáticamente (un Principal no tiene padre).
                        if (opt.id === 'principal' && parentId) {
                          setParentId('')
                        }
                      }}
                      className={
                        'relative text-left rounded-xl border-2 p-3 transition-all ' +
                        (active
                          ? 'border-expo-primary bg-expo-primary/10 shadow-sm'
                          : 'border-expo-line bg-white hover:border-expo-primary/50 hover:bg-expo-light/40')
                      }
                    >
                      {active && (
                        <Check
                          size={16}
                          className="absolute top-2 right-2 text-expo-primary"
                        />
                      )}
                      <div className="flex items-center gap-3">
                        <div
                          className={
                            'p-2 rounded-lg ' +
                            (active
                              ? 'bg-expo-primary text-white'
                              : 'bg-expo-light text-expo-primary')
                          }
                        >
                          <Icon size={20} />
                        </div>
                        <div>
                          <div className="font-semibold text-expo-dark">
                            {opt.label}
                          </div>
                          <div className="text-xs text-gray-500">
                            {opt.description}
                          </div>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </Field>

            {/* ─── Jerarquía ──────────────────────────────────────────── */}
            <div className="rounded-xl border border-expo-line bg-expo-light/30 p-4 space-y-3">
              <div className="text-sm font-semibold text-expo-dark flex items-center gap-2">
                <Link2 size={16} />
                Jerarquía (Principal → Consignatarios)
              </div>

              <Field label="Pertenece a (cliente Principal)">
                <Select
                  value={parentId}
                  onChange={(e) => {
                    const v = e.target.value
                    setParentId(v)
                    if (v) setChildIds([]) // un consignatario no puede tener consignatarios
                  }}
                  disabled={childIds.length > 0}
                >
                  <option value="">— Ninguno (cliente Principal) —</option>
                  {parentCandidates.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.id})
                    </option>
                  ))}
                </Select>
              </Field>
              {childIds.length > 0 && (
                <p className="text-xs text-gray-500 -mt-1">
                  Este cliente ya tiene consignatarios ligados, por eso no puede
                  a su vez pertenecer a otro.
                </p>
              )}

              {!parentId && (
                <div>
                  <div className="text-sm font-medium text-expo-dark mb-1">
                    Consignatarios ligados a este Principal
                  </div>
                  <div className="flex gap-2 mb-2">
                    <Select
                      className="flex-1"
                      value={childPick}
                      onChange={(e) => setChildPick(e.target.value)}
                    >
                      <option value="">Seleccionar consignatario…</option>
                      {childCandidates.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} ({c.id})
                        </option>
                      ))}
                    </Select>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={addChild}
                      disabled={!childPick}
                    >
                      <Plus size={14} />
                      <span>Añadir</span>
                    </Button>
                  </div>
                  {childIds.length === 0 ? (
                    <p className="text-xs text-gray-500">
                      Sin consignatarios ligados. Añade aquí las ubicaciones o
                      consignatarios a los que se envía directamente.
                    </p>
                  ) : (
                    <ul className="space-y-1">
                      {childIds.map((id) => {
                        const c = byId.get(id)
                        return (
                          <li
                            key={id}
                            className="flex items-center gap-2 bg-white rounded-lg border border-expo-line px-3 py-1.5 text-sm"
                          >
                            <span className="px-2 py-0.5 rounded-full text-[10px] bg-indigo-100 text-indigo-700 font-semibold">
                              {id}
                            </span>
                            <span className="font-medium text-expo-dark">
                              {c?.name || '(desconocido)'}
                            </span>
                            <button
                              type="button"
                              className="ml-auto p-1 text-gray-400 hover:text-red-600"
                              onClick={() => removeChild(id)}
                              title="Quitar"
                            >
                              <Link2Off size={14} />
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="secondary" onClick={closeModal}>
                <X size={16} />
                <span>Cancelar</span>
              </Button>
              <Button type="submit" disabled={save.loading}>
                {save.loading ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  )
}
